/**
 * Состояние плашки согласия — маленький общий store, потому что решение
 * принимают в одном месте, а отменяют в другом: плашка живёт в оболочке
 * приложения, а ссылка «изменить выбор» — на странице политики
 * конфиденциальности, и между ними нет и не должно быть родства по дереву.
 */
import { create } from 'zustand'
import { analyticsAvailable } from './config'
import { forgetConsent, readConsent, writeConsent, type ConsentChoice } from './consent'
import { ACHP_EVENTS } from './events'
import { track, updateConsent } from './gtag'

interface ConsentState {
  /** `null` — человека ещё не спрашивали или он попросил спросить снова. */
  choice: ConsentChoice | null
  /**
   * Показывать ли плашку. Отдельное поле, а не `choice === null`: до
   * первого эффекта в браузере ответа нет НИ У КОГО, в том числе у сборки
   * статики, — и плашка, отрисованная в предрендер, уехала бы в HTML, который
   * читает поисковик.
   */
  visible: boolean
  /** Первый заход в браузере: поднять сохранённый ответ. */
  hydrate(): void
  decide(choice: ConsentChoice): void
  /** «Изменить выбор» на `/privacy`. */
  reopen(): void
}

export const useConsentStore = create<ConsentState>((set) => ({
  choice: null,
  visible: false,

  hydrate() {
    // Нет аналитики — нет и разговора про согласие: плашка, которая ничего не
    // включает и не выключает, это ложь на экране.
    if (!analyticsAvailable()) {
      set({ choice: null, visible: false })
      return
    }
    const stored = readConsent()
    set({ choice: stored, visible: stored === null })
  },

  decide(choice) {
    writeConsent(choice)
    updateConsent(choice)
    set({ choice, visible: false })
    track(ACHP_EVENTS.consentChoice, { choice })
  },

  reopen() {
    forgetConsent()
    // Разрешение снимается СРАЗУ, не дожидаясь нового ответа: пока человек
    // выбирает заново, он находится в состоянии «не спрашивали», а это по
    // умолчанию запрет.
    updateConsent('necessary')
    set({ choice: null, visible: analyticsAvailable() })
  },
}))
